import React, { useState, useEffect, useMemo } from 'react';
import { getDatabase, ref, onValue, query, orderByChild } from 'firebase/database';
import { app } from '../firebase/config';
import { CSVLink } from 'react-csv';
import { FaDownload, FaChevronDown, FaChevronUp } from 'react-icons/fa';

const SalesReport = () => {
  const [salesData, setSalesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showTopCustomers, setShowTopCustomers] = useState(false);

  useEffect(() => {
    const db = getDatabase(app);
    const ordersRef = ref(db, 'HTAMS/orders');
    const salesQuery = query(ordersRef, orderByChild('orderDate'));

    const unsubscribe = onValue(salesQuery, (snapshot) => {
      const data = [];
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          data.push({ id: childSnapshot.key, ...childSnapshot.val() });
        });
      }
      setSalesData(data.reverse());
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Get available months
  const availableMonths = useMemo(() => {
    const monthsSet = new Set();
    salesData.forEach(sale => {
      const date = new Date(sale.orderDate);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      monthsSet.add(monthKey);
    });
    return Array.from(monthsSet).sort().reverse();
  }, [salesData]);

  const filteredData = useMemo(() => {
    let filtered = [...salesData];

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(sale => sale.status?.toLowerCase() === statusFilter.toLowerCase());
    }

    // Month filter
    if (selectedMonth !== 'all') {
      const [year, month] = selectedMonth.split('-');
      filtered = filtered.filter(sale => {
        const saleDate = new Date(sale.orderDate);
        return saleDate.getFullYear() === parseInt(year) &&
          saleDate.getMonth() === parseInt(month) - 1;
      });
    }

    return filtered;
  }, [salesData, selectedMonth, statusFilter]);

  const analytics = useMemo(() => {
    const totalOrders = filteredData.length;
    const totalRevenue = filteredData.reduce((sum, sale) => sum + (sale.totalAmount || 0), 0);
    const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

    // Status breakdown
    const statusBreakdown = filteredData.reduce((acc, sale) => {
      const status = sale.status || 'Unknown';
      if (!acc[status]) acc[status] = { count: 0, revenue: 0 };
      acc[status].count++;
      acc[status].revenue += sale.totalAmount || 0;
      return acc;
    }, {});

    // Product breakdown
    const productBreakdown = filteredData.reduce((acc, sale) => {
      if (sale.items && Array.isArray(sale.items)) {
        sale.items.forEach(item => {
          const product = item.productName || item.name || 'Unknown';
          if (!acc[product]) acc[product] = { count: 0, revenue: 0 };
          acc[product].count += item.quantity || 1;
          acc[product].revenue += item.totalPrice || item.total || 0;
        });
      }
      return acc;
    }, {});

    // Customer breakdown
    const customerBreakdown = filteredData.reduce((acc, sale) => {
      const customer = sale.customerName || 'Unknown';
      if (!acc[customer]) acc[customer] = { count: 0, revenue: 0 };
      acc[customer].count++;
      acc[customer].revenue += sale.totalAmount || 0;
      return acc;
    }, {});

    // State breakdown
    const stateBreakdown = filteredData.reduce((acc, sale) => {
      const state = sale.customerAddress?.state || sale.invoice?.customerState || 'Unknown';
      if (!acc[state]) acc[state] = { count: 0, revenue: 0 };
      acc[state].count++;
      acc[state].revenue += sale.totalAmount || 0;
      return acc;
    }, {});

    // District breakdown
    const districtBreakdown = filteredData.reduce((acc, sale) => {
      const district = sale.customerAddress?.city || sale.district || 'Unknown';
      const state = sale.customerAddress?.state || sale.invoice?.customerState || 'Unknown';
      if (!acc[district]) acc[district] = { count: 0, revenue: 0, state };
      acc[district].count++;
      acc[district].revenue += sale.totalAmount || 0;
      return acc;
    }, {});

    return {
      totalOrders,
      totalRevenue,
      avgOrderValue,
      statusBreakdown,
      productBreakdown,
      customerBreakdown,
      stateBreakdown,
      districtBreakdown,
    };
  }, [filteredData]);

  const getMonthName = (monthKey) => {
    if (monthKey === 'all') return 'All Months';
    const [year, month] = monthKey.split('-');
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  };

  // CSV data
  const csvData = filteredData.map(sale => ({
    orderId: sale.orderId || sale.id,
    customerName: sale.customerName,
    customerPhone: sale.customerPhone,
    city: sale.customerAddress?.city || '',
    state: sale.customerAddress?.state || '',
    orderDate: new Date(sale.orderDate).toLocaleString('en-IN'),
    products: sale.items?.map(item => `${item.productName || item.name} (${item.quantity})`).join(', ') || sale.productName,
    totalAmount: sale.totalAmount,
    status: sale.status,
  }));

  const csvHeaders = [
    { label: 'Order ID', key: 'orderId' },
    { label: 'Customer', key: 'customerName' },
    { label: 'Phone', key: 'customerPhone' },
    { label: 'City', key: 'city' },
    { label: 'State', key: 'state' },
    { label: 'Order Date', key: 'orderDate' },
    { label: 'Products', key: 'products' },
    { label: 'Amount', key: 'totalAmount' },
    { label: 'Status', key: 'status' },
  ];

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid #e2e8f0',
            borderTopColor: '#3b82f6',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
            margin: '0 auto 12px'
          }}></div>
          <p style={{ color: '#64748b', margin: 0 }}>Loading...</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div className="sales-report-compact">
      {/* Filters */}
      <div className="filters-bar">
        <select
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          className="filter-select"
        >
          <option value="all">All Months</option>
          {availableMonths.map(month => (
            <option key={month} value={month}>{getMonthName(month)}</option>
          ))}
        </select>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="filter-select"
        >
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>

        <CSVLink
          data={csvData}
          headers={csvHeaders}
          filename={`sales-report-${selectedMonth}-${new Date().toISOString().split('T')[0]}.csv`}
          className="export-btn"
        >
          <FaDownload /> Export
        </CSVLink>
      </div>

      {/* Stats */}
      <div className="stats-row">
        <div className="stat-compact">
          <div className="stat-label">TOTAL REVENUE</div>
          <div className="stat-value">₹{analytics.totalRevenue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</div>
        </div>
        <div className="stat-compact">
          <div className="stat-label">TOTAL ORDERS</div>
          <div className="stat-value">{analytics.totalOrders.toLocaleString('en-IN')}</div>
        </div>
        <div className="stat-compact">
          <div className="stat-label">AVG ORDER VALUE</div>
          <div className="stat-value">₹{analytics.avgOrderValue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</div>
        </div>
      </div>

      {/* Collapsible Sections */}
      <div className="collapsible-sections">
        {/* Top Customers */}
        <div className="collapsible-box">
          <div className="collapsible-header" onClick={() => setShowTopCustomers(!showTopCustomers)}>
            <span>Top 10 Customers</span>
            {showTopCustomers ? <FaChevronUp /> : <FaChevronDown />}
          </div>
          {showTopCustomers && (
            <div className="collapsible-content">
              <table>
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Customer</th>
                    <th>Orders</th>
                    <th>Revenue</th>
                    <th>Avg</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(analytics.customerBreakdown)
                    .sort((a, b) => b[1].revenue - a[1].revenue)
                    .slice(0, 10)
                    .map(([customer, data], index) => (
                      <tr key={customer}>
                        <td className="td-rank">#{index + 1}</td>
                        <td className="td-bold">{customer}</td>
                        <td>{data.count}</td>
                        <td className="td-revenue">₹{data.revenue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                        <td>₹{(data.revenue / data.count).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Orders Table */}
      <div className="orders-table-box">
        <div className="table-header">All Orders ({filteredData.length})</div>
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Customer</th>
                <th>Date</th>
                <th>Products</th>
                <th>Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.length > 0 ? (
                filteredData.map((sale) => {
                  const compactOrderId = sale.orderId ?
                    (sale.orderId.split('_').pop() || sale.orderId).toUpperCase() :
                    (sale.id || 'N/A');

                  const productList = sale.items?.map(item => ({
                    name: item.productName || item.name,
                    qty: item.quantity || 1
                  })) || [{ name: sale.productName || 'N/A', qty: 1 }];

                  return (
                    <tr key={sale.id || sale.orderId}>
                      <td className="td-order-id" title={sale.orderId || sale.id}>
                        #{compactOrderId.substring(0, 8)}
                      </td>
                      <td>
                        <div className="td-customer">{sale.customerName || 'N/A'}</div>
                        {sale.customerPhone && <div className="td-phone">{sale.customerPhone}</div>}
                      </td>
                      <td className="td-date">
                        {new Date(sale.orderDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </td>
                      <td>
                        {productList.map((item, idx) => (
                          <div key={idx} className="td-product">
                            {item.name} {item.qty > 1 && `×${item.qty}`}
                          </div>
                        ))}
                      </td>
                      <td className="td-amount">₹{sale.totalAmount?.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                      <td>
                        <span className={`status-badge ${sale.status?.toLowerCase() || 'unknown'}`}>
                          {sale.status || 'Unknown'}
                        </span>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="6" className="td-no-data">No sales data found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <style jsx>{`
        .sales-report-compact {
          max-width: 100%;
          margin: 0 auto;
        }

        .filters-bar {
          display: flex;
          gap: 12px;
          background: white;
          padding: 12px;
          border-radius: 6px;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
          margin-bottom: 16px;
        }

        .filter-select {
          flex: 1;
          padding: 8px 12px;
          border: 1px solid #e2e8f0;
          border-radius: 4px;
          font-size: 0.85rem;
          background: white;
          cursor: pointer;
        }

        .filter-select:focus {
          outline: none;
          border-color: #3b82f6;
        }

        .export-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          background: #10b981;
          color: white;
          border-radius: 4px;
          font-size: 0.85rem;
          font-weight: 600;
          text-decoration: none;
          cursor: pointer;
          transition: all 0.2s;
        }

        .export-btn:hover {
          background: #059669;
        }

        .stats-row {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
          margin-bottom: 16px;
        }

        .stat-compact {
          background: white;
          padding: 14px 16px;
          border-radius: 6px;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        }

        .stat-label {
          font-size: 0.7rem;
          font-weight: 700;
          color: #64748b;
          letter-spacing: 0.5px;
          margin-bottom: 6px;
        }

        .stat-value {
          font-size: 1.6rem;
          font-weight: 800;
          color: #1e293b;
        }

        .breakdowns-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
          margin-bottom: 16px;
        }

        .breakdown-box {
          background: white;
          padding: 12px;
          border-radius: 6px;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        }

        .breakdown-title {
          font-size: 0.85rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 10px;
          padding-bottom: 8px;
          border-bottom: 1px solid #e2e8f0;
        }

        .breakdown-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .breakdown-item {
          display: grid;
          grid-template-columns: auto 1fr auto auto;
          gap: 10px;
          align-items: center;
          padding: 8px;
          background: #f8fafc;
          border-radius: 4px;
          font-size: 0.8rem;
        }

        .status-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          flex-shrink: 0;
        }

        .status-dot.pending { background: #f59e0b; }
        .status-dot.completed { background: #10b981; }
        .status-dot.cancelled { background: #ef4444; }
        .status-dot.unknown { background: #94a3b8; }

        .product-rank {
          font-weight: 700;
          color: #3b82f6;
        }

        .breakdown-name {
          font-weight: 600;
          color: #1e293b;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .breakdown-count {
          color: #64748b;
          font-size: 0.75rem;
        }

        .breakdown-revenue {
          font-weight: 700;
          color: #10b981;
          text-align: right;
        }

        .collapsible-sections {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-bottom: 16px;
        }

        .collapsible-box {
          background: white;
          border-radius: 6px;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
          overflow: hidden;
        }

        .collapsible-header {
          padding: 12px 16px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          cursor: pointer;
          font-size: 0.85rem;
          font-weight: 700;
          color: #1e293b;
          background: white;
          transition: background 0.2s;
        }

        .collapsible-header:hover {
          background: #f8fafc;
        }

        .collapsible-content {
          padding: 12px;
          border-top: 1px solid #e2e8f0;
        }

        .orders-table-box {
          background: white;
          padding: 12px;
          border-radius: 6px;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        }

        .table-header {
          font-size: 0.85rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 10px;
          padding-bottom: 8px;
          border-bottom: 1px solid #e2e8f0;
        }

        .table-wrapper {
          overflow-x: auto;
          border: 1px solid #e2e8f0;
          border-radius: 4px;
        }

        table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.8rem;
        }

        thead {
          background: #f8fafc;
        }

        th {
          text-align: left;
          padding: 10px 12px;
          font-weight: 700;
          color: #475569;
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.3px;
          border-bottom: 1px solid #e2e8f0;
        }

        td {
          padding: 10px 12px;
          border-bottom: 1px solid #f1f5f9;
          color: #1e293b;
        }

        tbody tr:hover {
          background: #f8fafc;
        }

        tbody tr:last-child td {
          border-bottom: none;
        }

        .td-order-id {
          font-family: monospace;
          font-weight: 700;
          color: #3b82f6;
          font-size: 0.75rem;
        }

        .td-customer {
          font-weight: 600;
          font-size: 0.85rem;
        }

        .td-phone {
          font-size: 0.7rem;
          color: #64748b;
          margin-top: 2px;
        }

        .td-date {
          color: #64748b;
          font-size: 0.75rem;
        }

        .td-product {
          font-size: 0.75rem;
          padding: 2px 0;
          color: #475569;
        }

        .td-amount {
          font-weight: 700;
          color: #10b981;
          font-size: 0.85rem;
        }

        .td-bold {
          font-weight: 600;
        }

        .td-muted {
          color: #94a3b8;
          font-size: 0.75rem;
        }

        .td-revenue {
          font-weight: 700;
          color: #10b981;
        }

        .td-percent {
          color: #3b82f6;
          font-weight: 600;
        }

        .td-rank {
          color: #3b82f6;
          font-weight: 700;
        }

        .td-no-data {
          text-align: center;
          padding: 40px !important;
          color: #94a3b8;
        }

        .status-badge {
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 0.7rem;
          font-weight: 600;
          display: inline-block;
          white-space: nowrap;
        }

        .status-badge.pending {
          background: #fef3c7;
          color: #92400e;
        }

        .status-badge.completed {
          background: #d1fae5;
          color: #065f46;
        }

        .status-badge.cancelled {
          background: #fee2e2;
          color: #991b1b;
        }

        .status-badge.unknown {
          background: #e5e7eb;
          color: #6b7280;
        }

        @media (max-width: 1024px) {
          .breakdowns-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 768px) {
          .stats-row {
            grid-template-columns: 1fr;
          }
          .filters-bar {
            flex-direction: column;
          }
        }
      `}</style>
    </div>
  );
};

export default SalesReport;
